import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AssessmentIcon from '@mui/icons-material/Assessment'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import MicIcon from '@mui/icons-material/Mic'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import SpeedIcon from '@mui/icons-material/Speed'
import LanguageIcon from '@mui/icons-material/Language'
import BarChartIcon from '@mui/icons-material/BarChart'
import CampaignIcon from '@mui/icons-material/Campaign'
import ConstructionIcon from '@mui/icons-material/Construction'
import { useAuth } from '../App'
import UsageChart from './UsageChart'
import EmptyState from './EmptyState'
import { useTranslation } from 'react-i18next'
import UsageOverviewTab from './usage/UsageOverviewTab'
import UsageChartsTab from './usage/UsageChartsTab'
import UsageEnginesTab from './usage/UsageEnginesTab'

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

interface UsageModalProps {
  open: boolean
  onClose: () => void
}

interface UsageStats {
  totalRequests: number
  totalMinutes: number
  totalCost: number
  currentBalance: number
  thisMonth: {
    requests: number
    minutes: number
    cost: number
  }
  lastMonth: {
    requests: number
    minutes: number
    cost: number
  }
  dailyUsage: {
    date: string
    requests: number
    minutes: number
    cost: number
    httpRequests?: number
    websocketRequests?: number
    diarizationRequests?: number
    asyncRequests?: number
    failedRequests?: number
    avgProcessingTime?: number
  }[]
  avgProcessingTime?: number
}

interface EngineUsage {
  engineName: string
  requests: number
  minutes: number
  cost: number
  percentage: number
}

interface DetailedUsageAnalytics {
  summary: {
    totalRequests: number
    totalDurationSeconds: number
    totalCostMicroeuros: number
    avgProcessingTime: number
  }
  timeSeriesData: {
    date: string
    requests: number
    durationMinutes: number
    cost: number
    httpRequests: number
    websocketRequests: number
    diarizationRequests: number
    asyncRequests: number
    failedRequests: number
    avgProcessingTime: number
  }[]
  engineDistribution: {
    name: string
    value: number
    requests: number
    durationMinutes: number
  }[]
  featureUsage: {
    http: number
    websocket: number
    diarization: number
    async: number
  }
  performanceMetrics: {
    avgProcessingTime: number
    avgQueueTime: number
    errorRate: number
  }
  period: {
    startDate: string
    endDate: string
    days: number
    granularity: string
  }
}

interface ApiKeyUsageAnalytics {
  period: {
    startDate: string
    endDate: string
    days: number
  }
  summary: {
    totalApiKeys: number
    totalRequests: number
    totalCostMicroeuros: number
    totalCostEuros: number
    hasDirectAuth: boolean
  }
  apiKeyBreakdown: {
    apiKeyId: number | null
    apiKeyName: string
    keyPreview: string | null
    requests: number
    durationSeconds: number
    costMicroeuros: number
    costEuros: number
    avgProcessingTimeMs: number
    failedRequests: number
    requestsPercentage: number
    costPercentage: number
  }[]
}

// ── formatTrend helper ────────────────────────────────────────────
function formatTrend(
  current: number,
  previous: number
): { label: string; direction: 'up' | 'down' | 'flat'; color: string } {
  const cur = current || 0
  const prev = previous || 0
  if (prev === 0) {
    return { label: '—', direction: 'flat', color: 'text.secondary' }
  }
  const pct = ((cur - prev) / prev) * 100
  if (pct === 0) {
    return { label: '0%', direction: 'flat', color: 'text.secondary' }
  }
  const sign = pct > 0 ? '+' : ''
  const label = `${sign}${pct.toFixed(1)}%`
  const direction: 'up' | 'down' = pct > 0 ? 'up' : 'down'
  const color = direction === 'up' ? 'success.main' : 'error.main'
  return { label, direction, color }
}

const UsageModal: React.FC<UsageModalProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null)
  const [engineUsage, setEngineUsage] = useState<EngineUsage[]>([])
  const [detailedAnalytics, setDetailedAnalytics] = useState<DetailedUsageAnalytics | null>(null)
  const [apiKeyAnalytics, setApiKeyAnalytics] = useState<ApiKeyUsageAnalytics | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tabValue, setTabValue] = useState(0)
  const [timeRange, setTimeRange] = useState(30) // days
  const [serviceTab, setServiceTab] = useState(0) // 0=ASR, 1=TTS
  const auth = useAuth()
  const { t } = useTranslation()

  const baseURL = API_BASE

  useEffect(() => {
    if (open) {
      fetchUsageData()
    }
  }, [open, timeRange])

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleTimeRangeChange = (event: any) => {
    setTimeRange(event.target.value)
  }

  const fetchUsageData = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = await auth.getToken()

      // Fetch basic usage statistics
      const statsResponse = await fetch(`${baseURL}/usage/stats?days=${timeRange}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (statsResponse.ok) {
        const statsData = await statsResponse.json()
        // Ensure all required fields have default values
        const normalizedStats = {
          totalRequests: statsData.totalRequests || 0,
          totalMinutes: statsData.totalMinutes || 0,
          totalCost: statsData.totalCost || 0,
          currentBalance: statsData.currentBalance || 0,
          thisMonth: {
            requests: statsData.thisMonth?.requests || 0,
            minutes: statsData.thisMonth?.minutes || 0,
            cost: statsData.thisMonth?.cost || 0
          },
          lastMonth: {
            requests: statsData.lastMonth?.requests || 0,
            minutes: statsData.lastMonth?.minutes || 0,
            cost: statsData.lastMonth?.cost || 0
          },
          dailyUsage: statsData.dailyUsage || [],
          avgProcessingTime: statsData.avgProcessingTime || 0
        }
        setUsageStats(normalizedStats)
      } else {
        const errorText = await statsResponse.text()
        console.error('Stats API error:', statsResponse.status, statsResponse.statusText, errorText)
        setError(`Failed to load stats: ${statsResponse.status} ${statsResponse.statusText}`)
        return
      }

      // Fetch engine usage breakdown
      const engineResponse = await fetch(`${baseURL}/usage/engines?days=${timeRange}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (engineResponse.ok) {
        const engineData = await engineResponse.json()
        setEngineUsage(engineData)
      } else {
        const errorText = await engineResponse.text()
        console.error('Engine API error:', engineResponse.status, engineResponse.statusText, errorText)
      }

      // Fetch detailed analytics for charts
      const detailedResponse = await fetch(`${baseURL}/usage/detailed?days=${timeRange}&granularity=daily`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (detailedResponse.ok) {
        const detailedData = await detailedResponse.json()
        setDetailedAnalytics(detailedData)
      } else {
        const errorText = await detailedResponse.text()
        console.error('Detailed API error:', detailedResponse.status, detailedResponse.statusText, errorText)
      }

      // Fetch API key analytics
      const apiKeyResponse = await fetch(`${baseURL}/usage/by-key?days=${timeRange}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (apiKeyResponse.ok) {
        const apiKeyData = await apiKeyResponse.json()
        setApiKeyAnalytics(apiKeyData)
      } else {
        const errorText = await apiKeyResponse.text()
        console.error('API Key analytics error:', apiKeyResponse.status, apiKeyResponse.statusText, errorText)
      }

    } catch (err) {
      setError('Failed to load usage data')
      console.error('Usage data fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const mins = Math.round(minutes % 60)
    if (hours > 0) {
      return `${hours}h ${mins}m`
    }
    return `${mins}m`
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth aria-labelledby="usage-dialog-title">
      <DialogTitle id="usage-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <AssessmentIcon sx={{ mr: 1 }} aria-hidden="true" />
            <Typography variant="h6">{t('usageAnalytics', 'Usage Analytics')}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="usage-time-range-label">{t('timeRange', 'Time Range')}</InputLabel>
              <Select
                labelId="usage-time-range-label"
                value={timeRange}
                label={t('timeRange', 'Time Range')}
                onChange={handleTimeRangeChange}
              >
                <MenuItem value={7}>7 {t('days', 'days')}</MenuItem>
                <MenuItem value={14}>14 {t('days', 'days')}</MenuItem>
                <MenuItem value={30}>30 {t('days', 'days')}</MenuItem>
                <MenuItem value={90}>90 {t('days', 'days')}</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={onClose} aria-label={t('common.close', 'Close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 3, mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* ASR / TTS service selector */}
        <Box sx={{ px: 3, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={serviceTab}
            exclusive
            onChange={(_e, val) => { if (val !== null) setServiceTab(val) }}
            size="small"
            aria-label={t('serviceSelector', 'Service selector')}
          >
            <ToggleButton
              value={0}
              aria-label={t('asr', 'ASR')}
              sx={{
                px: 3,
                fontWeight: 700,
                '&.Mui-selected': { backgroundColor: 'rgba(0, 125, 138, 0.12)', color: '#007d8a', borderColor: '#007d8a' },
                '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 125, 138, 0.2)' }
              }}
            >
              {t('asr', 'ASR')}
            </ToggleButton>
            <ToggleButton
              value={1}
              aria-label={t('tts', 'TTS')}
              sx={{
                px: 3,
                fontWeight: 700,
                '&.Mui-selected': { backgroundColor: 'rgba(0, 74, 87, 0.15)', color: '#004a57', borderColor: '#004a57' },
                '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 74, 87, 0.25)' }
              }}
            >
              {t('tts', 'TTS')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* TTS View — analytics coming soon empty state */}
        {serviceTab === 1 && (
          <Box sx={{ p: 3 }}>
            <EmptyState
              icon={<ConstructionIcon />}
              title={t('ttsAnalyticsComingSoon', 'TTS Analytics Coming Soon')}
              description={t(
                'ttsAnalyticsDescription',
                "We're building detailed TTS usage tracking. Check back soon."
              )}
            />
          </Box>
        )}

        {/* ASR View */}
        {serviceTab === 0 && loading ? (
          <Box display="flex" justifyContent="center" p={6}>
            <CircularProgress />
          </Box>
        ) : serviceTab === 0 && usageStats ? (
          <>
            {/* Overview Cards */}
            <Box sx={{ p: 3, pb: 0 }}>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05), rgba(25, 118, 210, 0.1))' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography color="textSecondary" gutterBottom variant="body2">
                            {t('totalRequests', 'Total Requests')}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#007d8a' }}>
                            {usageStats.totalRequests.toLocaleString()}
                          </Typography>
                        </Box>
                        <MicIcon sx={{ fontSize: 40, color: '#007d8a', opacity: 0.7 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05), rgba(76, 175, 80, 0.1))' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography color="textSecondary" gutterBottom variant="body2">
                            {t('totalMinutes', 'Total Minutes')}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50' }}>
                            {formatTime(usageStats.totalMinutes)}
                          </Typography>
                        </Box>
                        <AccessTimeIcon sx={{ fontSize: 40, color: '#4caf50', opacity: 0.7 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.05), rgba(255, 152, 0, 0.1))' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography color="textSecondary" gutterBottom variant="body2">
                            {t('totalSpent', 'Total Spent')}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                            €{(usageStats.totalCost || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <TrendingUpIcon sx={{ fontSize: 40, color: 'warning.dark', opacity: 0.7 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.05), rgba(156, 39, 176, 0.1))' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography color="textSecondary" gutterBottom variant="body2">
                            {t('avgProcessing', 'Avg Processing')}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#004a57' }}>
                            {usageStats.avgProcessingTime ? Math.round(usageStats.avgProcessingTime) : 0}ms
                          </Typography>
                        </Box>
                        <SpeedIcon sx={{ fontSize: 40, color: '#004a57', opacity: 0.7 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Tabs for different views */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="usage analytics tabs">
                <Tab label={t('overview', 'Overview')} />
                <Tab label={t('charts', 'Charts')} />
                <Tab label={t('engines', 'Engines')} />
                <Tab label={t('performance', 'Performance')} />
                <Tab label={t('apiKeys', 'API Keys')} />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ p: 3 }}>
              {/* Overview Tab */}
              {tabValue === 0 && (
                <UsageOverviewTab
                  thisMonth={usageStats.thisMonth}
                  lastMonth={usageStats.lastMonth}
                  formatTime={formatTime}
                  formatTrend={formatTrend}
                />
              )}

              {/* Charts Tab */}
              {tabValue === 1 && detailedAnalytics && (
                <UsageChartsTab timeSeriesData={detailedAnalytics.timeSeriesData} />
              )}

              {/* Engines Tab */}
              {tabValue === 2 && (
                <UsageEnginesTab engineUsage={engineUsage} formatTime={formatTime} />
              )}

              {/* Performance Tab */}
              {tabValue === 3 && detailedAnalytics && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <UsageChart
                      dailyUsage={detailedAnalytics.timeSeriesData}
                      engineUsage={[]}
                      type="performance"
                      title={t('performanceMetrics', 'Performance Metrics')}
                      height={350}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ textAlign: 'center', background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05), rgba(33, 150, 243, 0.1))' }}>
                      <CardContent>
                        <SpeedIcon sx={{ fontSize: 48, color: '#2196f3', mb: 2 }} />
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>
                          {Math.round(detailedAnalytics.performanceMetrics.avgProcessingTime)}ms
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('avgProcessingTime', 'Avg Processing Time')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ textAlign: 'center', background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05), rgba(76, 175, 80, 0.1))' }}>
                      <CardContent>
                        <BarChartIcon sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                          {(100 - detailedAnalytics.performanceMetrics.errorRate).toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('successRate', 'Success Rate')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ textAlign: 'center', background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.05), rgba(255, 193, 7, 0.1))' }}>
                      <CardContent>
                        <AccessTimeIcon sx={{ fontSize: 48, color: '#b45309', mb: 2 }} />
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#b45309' }}>
                          {Math.round(detailedAnalytics.performanceMetrics.avgQueueTime)}ms
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('avgQueueTime', 'Avg Queue Time')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* API Keys Tab */}
              {tabValue === 4 && apiKeyAnalytics && (
                <Grid container spacing={3}>
                  {/* Summary Cards */}
                  <Grid item xs={12}>
                    <Card sx={{ background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.02), rgba(25, 118, 210, 0.05))' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1a237e' }}>
                          {t('apiKeySummary', 'API Key Usage Summary')}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="textSecondary">
                              {t('totalApiKeys', 'Total API Keys')}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#007d8a' }}>
                              {apiKeyAnalytics.summary.totalApiKeys}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="textSecondary">
                              {t('totalRequests', 'Total Requests')}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#4caf50' }}>
                              {apiKeyAnalytics.summary.totalRequests.toLocaleString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="textSecondary">
                              {t('totalCost', 'Total Cost')}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                              €{apiKeyAnalytics.summary.totalCostEuros.toFixed(4)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="textSecondary">
                              {t('authMethods', 'Auth Methods')}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#004a57' }}>
                              {apiKeyAnalytics.summary.hasDirectAuth ? 'API + Direct' : 'API Keys Only'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* API Key Breakdown */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1a237e' }}>
                          {t('apiKeyBreakdown', 'API Key Breakdown')}
                        </Typography>
                        {apiKeyAnalytics.apiKeyBreakdown.map((apiKey, index) => (
                          <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {apiKey.apiKeyName}
                                </Typography>
                                {apiKey.keyPreview && (
                                  <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                                    {apiKey.keyPreview}
                                  </Typography>
                                )}
                              </Box>
                              <Box textAlign="right">
                                <Chip
                                  label={`${apiKey.costPercentage.toFixed(1)}% of costs`}
                                  color="primary"
                                  variant="outlined"
                                  size="small"
                                />
                              </Box>
                            </Box>

                            <LinearProgress
                              variant="determinate"
                              value={apiKey.costPercentage}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                mb: 2,
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4
                                }
                              }}
                            />

                            <Grid container spacing={2}>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="textSecondary">
                                  {t('requests', 'Requests')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {apiKey.requests.toLocaleString()}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="textSecondary">
                                  {t('duration', 'Duration')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {formatTime(apiKey.durationSeconds / 60)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="textSecondary">
                                  {t('cost', 'Cost')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  €{apiKey.costEuros.toFixed(4)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="textSecondary">
                                  {t('avgProcessing', 'Avg Processing')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {apiKey.avgProcessingTimeMs}ms
                                </Typography>
                              </Grid>
                              {apiKey.failedRequests > 0 && (
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="error">
                                    {t('failedRequests', 'Failed Requests')}: {apiKey.failedRequests}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                            {index < apiKeyAnalytics.apiKeyBreakdown.length - 1 && <Divider sx={{ mt: 2 }} />}
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          </>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default UsageModal
